import { useMutation, useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  GeneratorStatusExceedanceStatus,
  RcraInfoFederalWasteGeneratorStatus,
} from "generated-graphql/graphql";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  CreateGeneratorStatusChangeSubmissionsDialog,
  GeneratorStatusChangeFacility,
} from "./CreateGeneratorStatusChangeSubmissionsDialog";
import { ExceedanceStatusChip } from "./ExceedanceStatusChip";

type LinkedSubmissionSummary = {
  id: string;
  title: string;
  submissionType?: string;
  status: string;
  dueDate?: string | null;
};

export type ExceedanceForDrawer = {
  facilityId: string;
  tenantId: string;
  facilityName: string;
  organization: string;
  state: string | null;
  source: string;
  periodStart: string;
  reportedFederalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedFederalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  changeExplanation: string;
  resolutionStatus: GeneratorStatusExceedanceStatus;
  openSubmission?: LinkedSubmissionSummary | null;
  linkedSubmission?: LinkedSubmissionSummary | null;
};

type ExceedanceKey = {
  facilityId: string;
  source: string;
  periodStart: string;
  reportedFederalStatus: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedFederalStatus: RcraInfoFederalWasteGeneratorStatus | null;
};

const DETAIL_QUERY = gql(`
  query GeneratorStatusExceedanceDetail($facilityId: ID!, $source: String!, $periodStart: Date!) {
    generatorStatusExceedanceDetail(facilityId: $facilityId, source: $source, periodStart: $periodStart) {
      facilityId
      source
      periodStart
      determinationFactors
      resolution {
        id
        status
        resolutionType
        note
        resolvedAt
      }
      events {
        id
        eventType
        status
        resolutionType
        note
        actorId
        actorName
        createdAt
      }
      wadmByMonth {
        periodStart
        totalWeightLbs
        itemCount
        wadmGeneratorStatus
      }
    }
  }
`);

const DISMISS_MUTATION = gql(`
  mutation DismissGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $reason: String
  ) {
    dismissGeneratorStatusExceedances(exceedances: $exceedances, reason: $reason) {
      id
      status
    }
  }
`);

const MARK_RESOLVED_MUTATION = gql(`
  mutation MarkGeneratorStatusExceedancesResolved(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $note: String
    $submissionId: ID
  ) {
    markGeneratorStatusExceedancesResolved(
      exceedances: $exceedances
      note: $note
      submissionId: $submissionId
    ) {
      id
      status
    }
  }
`);

const LINKABLE_SUBMISSIONS_QUERY = gql(`
  query GeneratorStatusExceedanceLinkableSubmissions($facilityId: ID!, $search: String) {
    generatorStatusExceedanceLinkableSubmissions(facilityId: $facilityId, search: $search) {
      id
      title
      submissionType
      status
      dueDate
      createdAt
      facilityName
      assigneeName
    }
  }
`);

const REOPEN_MUTATION = gql(`
  mutation ReopenGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $note: String
  ) {
    reopenGeneratorStatusExceedances(exceedances: $exceedances, note: $note) {
      id
      status
    }
  }
`);

const REFETCH = ["StaffGeneratorStatusChanges", "GeneratorStatusExceedanceDetail"];

function formatMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function statusLabel(status: string): string {
  return prettyPrintEnumValue(status);
}

type Props = {
  exceedance: ExceedanceForDrawer | null;
  onClose: () => void;
  /** Called after a resolution action so the caller can refetch + clear state. */
  onResolved: () => void;
};

export function ExceedanceActionDrawer({ exceedance, onClose, onResolved }: Props) {
  const open = exceedance !== null;
  const [notePanel, setNotePanel] = useState<"dismiss" | "reset" | null>(null);
  const [noteText, setNoteText] = useState("");
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [markResolvedOpen, setMarkResolvedOpen] = useState(false);

  const { data } = useQuery(DETAIL_QUERY, {
    variables: exceedance
      ? {
          facilityId: exceedance.facilityId,
          source: exceedance.source,
          periodStart: exceedance.periodStart,
        }
      : { facilityId: "", source: "", periodStart: "" },
    skip: !exceedance,
    fetchPolicy: "cache-and-network",
  });

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const [reopen, { loading: reopening }] = useMutation(REOPEN_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });

  const busy = dismissing || reopening;

  const exceedanceKey = useMemo(
    () =>
      exceedance
        ? {
            facilityId: exceedance.facilityId,
            source: exceedance.source,
            periodStart: exceedance.periodStart,
            reportedFederalStatus: exceedance.reportedFederalStatus ?? null,
            calculatedFederalStatus: exceedance.calculatedFederalStatus ?? null,
          }
        : null,
    [exceedance]
  );

  const detail = data?.generatorStatusExceedanceDetail;
  const federalFactors = (detail?.determinationFactors as { federal?: Record<string, number> })
    ?.federal;

  const closeAndReset = () => {
    setNotePanel(null);
    setNoteText("");
    onClose();
  };

  const runDismiss = async () => {
    if (!exceedanceKey) return;
    await dismiss({ variables: { exceedances: [exceedanceKey], reason: noteText } });
    setNotePanel(null);
    setNoteText("");
    onResolved();
  };

  // Reopen/reset back to UNRESOLVED — also unlinks any linked submission. The
  // optional reason is recorded on the audit event.
  const runReset = async () => {
    if (!exceedanceKey) return;
    await reopen({ variables: { exceedances: [exceedanceKey], note: noteText || undefined } });
    setNotePanel(null);
    setNoteText("");
    onResolved();
  };

  const isResolved =
    exceedance?.resolutionStatus === GeneratorStatusExceedanceStatus.Resolved ||
    exceedance?.resolutionStatus === GeneratorStatusExceedanceStatus.Ignored;
  const isPending = exceedance?.resolutionStatus === GeneratorStatusExceedanceStatus.Pending;
  const linkedOrOpenSubmission = exceedance?.linkedSubmission ?? exceedance?.openSubmission ?? null;
  // A linked/open submission means the reset also unlinks it.
  const resetLabel = linkedOrOpenSubmission ? "Unlink & reset" : "Reopen";

  const facilityForFiling: GeneratorStatusChangeFacility[] = exceedance
    ? [
        {
          facilityId: exceedance.facilityId,
          tenantId: exceedance.tenantId,
          facilityName: exceedance.facilityName,
          organization: exceedance.organization,
          state: exceedance.state,
          periodStart: exceedance.periodStart,
        },
      ]
    : [];

  const footer = exceedance ? (
    <Stack spacing={1.5}>
      {notePanel ? (
        <Stack spacing={1}>
          <TextField
            label={
              notePanel === "dismiss"
                ? "Reason (why is this not a discrepancy?)"
                : `Reason (optional)${linkedOrOpenSubmission ? " — this unlinks the submission" : ""}`
            }
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            size="small"
            multiline
            minRows={2}
            autoFocus
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setNotePanel(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={notePanel === "dismiss" ? runDismiss : runReset}
              disabled={busy}
            >
              {notePanel === "dismiss" ? "Dismiss discrepancy" : "Reset to unresolved"}
            </Button>
          </Stack>
        </Stack>
      ) : isResolved ? (
        <Button variant="outlined" onClick={() => setNotePanel("reset")} disabled={busy}>
          Reopen
        </Button>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {/* When something is already being worked (PENDING) we don't offer to
              file another notification — only to resolve, dismiss, or unlink/reset. */}
          {!isPending && (
            <Button variant="contained" onClick={() => setFileDialogOpen(true)} disabled={busy}>
              File notification
            </Button>
          )}
          <Button variant="outlined" onClick={() => setMarkResolvedOpen(true)} disabled={busy}>
            Mark resolved
          </Button>
          {isPending && (
            <Button variant="outlined" onClick={() => setNotePanel("reset")} disabled={busy}>
              {resetLabel}
            </Button>
          )}
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setNotePanel("dismiss")}
            disabled={busy}
          >
            Dismiss
          </Button>
        </Stack>
      )}
    </Stack>
  ) : null;

  return (
    <>
      <RightDrawerShell
        open={open}
        onClose={closeAndReset}
        title={exceedance ? exceedance.facilityName : ""}
        footer={footer}
      >
        {exceedance && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ExceedanceStatusChip
                status={exceedance.resolutionStatus}
                label={statusLabel(exceedance.resolutionStatus)}
              />
              <Typography variant="body2" color="text.secondary">
                {exceedance.organization} · {exceedance.state ?? "—"} · {exceedance.source} ·{" "}
                {formatMonth(exceedance.periodStart)}
              </Typography>
            </Stack>

            {/* Discrepancy */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Potential discrepancy
              </Typography>
              <Typography variant="body2">
                Reported{" "}
                <strong>
                  {exceedance.reportedFederalStatus
                    ? statusLabel(exceedance.reportedFederalStatus)
                    : "—"}
                </strong>{" "}
                → calculated{" "}
                <strong>
                  {exceedance.calculatedFederalStatus
                    ? statusLabel(exceedance.calculatedFederalStatus)
                    : "—"}
                </strong>
              </Typography>
              {exceedance.changeExplanation && (
                <Typography variant="body2" color="text.secondary">
                  {exceedance.changeExplanation}
                </Typography>
              )}
              {federalFactors && (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableBody>
                    {Object.entries(federalFactors).map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell sx={{ border: 0, py: 0.25, color: "text.secondary" }}>
                          {k}
                        </TableCell>
                        <TableCell sx={{ border: 0, py: 0.25 }} align="right">
                          {v.toLocaleString("en-US", { maximumFractionDigits: 1 })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            {/* Linked submission — an explicitly linked submission (any type) or
                the auto-detected open notification driving the status. */}
            {linkedOrOpenSubmission && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Linked submission
                </Typography>
                <Link
                  to={`/staff/fulfillment/waste/submissions/${linkedOrOpenSubmission.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  <OpenInNew fontSize="small" />
                  {linkedOrOpenSubmission.title} ({getStatusLabel(linkedOrOpenSubmission.status)})
                </Link>
              </Box>
            )}

            {/* WADM accumulation — only rendered when the facility actually has
                hazardous WADM data. The WADM feed is Amazon-only, so this section
                is hidden entirely (no header, no empty state) for everyone else. */}
            {detail && detail.wadmByMonth.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  WADM — hazardous waste accumulation (month by month)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Weight (lbs)</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell>WADM status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.wadmByMonth.map((m) => (
                      <TableRow key={m.periodStart}>
                        <TableCell>{formatMonth(m.periodStart)}</TableCell>
                        <TableCell align="right">
                          {m.totalWeightLbs.toLocaleString("en-US", { maximumFractionDigits: 1 })}
                        </TableCell>
                        <TableCell align="right">{m.itemCount}</TableCell>
                        <TableCell>{m.wadmGeneratorStatus ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* History */}
            {detail && detail.events.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Resolution history
                </Typography>
                <Stack spacing={0.5}>
                  {detail.events.map((ev) => (
                    <Typography key={ev.id} variant="caption" color="text.secondary">
                      {new Date(ev.createdAt).toLocaleString("en-US")} — {statusLabel(ev.eventType)}
                      {" by "}
                      <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                        {ev.actorName ?? "—"}
                      </Box>
                      {ev.note ? `: ${ev.note}` : ""}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {exceedance.resolutionStatus === GeneratorStatusExceedanceStatus.Pending && (
              <Alert severity="info" variant="outlined">
                A notification has been filed for this facility — this discrepancy is being worked.
              </Alert>
            )}
          </Stack>
        )}
      </RightDrawerShell>

      <CreateGeneratorStatusChangeSubmissionsDialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
        facilities={facilityForFiling}
        onSuccess={() => {
          setFileDialogOpen(false);
          onResolved();
        }}
      />

      {exceedanceKey && exceedance && (
        <MarkResolvedDialog
          open={markResolvedOpen}
          onClose={() => setMarkResolvedOpen(false)}
          exceedanceKey={exceedanceKey}
          facilityId={exceedance.facilityId}
          onResolved={() => {
            setMarkResolvedOpen(false);
            onResolved();
          }}
        />
      )}
    </>
  );
}

type LinkableSubmissionOption = {
  id: string;
  title: string;
  submissionType: string;
  status: string;
  dueDate?: string | null;
  createdAt: string;
  facilityName?: string | null;
  assigneeName?: string | null;
};

type MarkResolvedDialogProps = {
  open: boolean;
  onClose: () => void;
  exceedanceKey: ExceedanceKey;
  facilityId: string;
  onResolved: () => void;
};

/**
 * "Mark resolved" dialog. Two paths:
 *  - Corrected elsewhere: acknowledge with an optional note, no submission.
 *  - Link to an existing submission: pick one of the facility's recent submissions
 *    (e.g. an in-progress notification for a consecutive-month exceedance, or an
 *    "Other"-typed memo to file). The exceedance then tracks that submission's
 *    status instead of being flatly "resolved".
 */
function MarkResolvedDialog({
  open,
  onClose,
  exceedanceKey,
  facilityId,
  onResolved,
}: MarkResolvedDialogProps) {
  const [mode, setMode] = useState<"acknowledge" | "link">("acknowledge");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState<LinkableSubmissionOption | null>(null);

  const { data, loading: loadingOptions } = useQuery(LINKABLE_SUBMISSIONS_QUERY, {
    variables: { facilityId },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const [markResolved, { loading: submitting }] = useMutation(MARK_RESOLVED_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });

  const options: LinkableSubmissionOption[] =
    data?.generatorStatusExceedanceLinkableSubmissions ?? [];

  const reset = () => {
    setMode("acknowledge");
    setNote("");
    setSelected(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    await markResolved({
      variables: {
        exceedances: [exceedanceKey],
        note: note || undefined,
        submissionId: mode === "link" ? selected?.id : undefined,
      },
    });
    reset();
    onResolved();
  };

  const submitDisabled = submitting || (mode === "link" && !selected);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 560, maxWidth: "90vw" } } }}
      title="Mark discrepancy resolved"
      description="Acknowledge this discrepancy, or link it to an existing submission so its status tracks that filing."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading: submitting,
        loadingText: "Saving...",
        hideStartIcon: true,
        saveText: "Mark resolved",
        disabled: submitDisabled,
      }}
      cancelButtonProps={{ disabled: submitting }}
    >
      <RadioGroup
        value={mode}
        onChange={(e) => setMode(e.target.value === "link" ? "link" : "acknowledge")}
      >
        <FormControlLabel
          value="acknowledge"
          control={<Radio size="small" />}
          label="Corrected elsewhere — no submission needed"
        />
        <FormControlLabel
          value="link"
          control={<Radio size="small" />}
          label="Link to an existing submission"
        />
      </RadioGroup>

      {mode === "link" && (
        <Autocomplete<LinkableSubmissionOption>
          options={options}
          loading={loadingOptions}
          value={selected}
          onChange={(_e, value) => setSelected(value)}
          getOptionLabel={(option) => option.title}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            const created = new Date(option.createdAt).toLocaleDateString("en-US");
            const meta = [
              option.facilityName,
              `Created ${created}`,
              option.assigneeName ?? "Unassigned",
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <li key={key} {...optionProps}>
                <Stack spacing={0.25} sx={{ py: 0.5 }}>
                  <Typography variant="body2">{option.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {prettyPrintEnumValue(option.submissionType)} · {getStatusLabel(option.status)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {meta}
                  </Typography>
                </Stack>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Submission" size="small" autoFocus />
          )}
          sx={{ mt: 1 }}
        />
      )}

      <TextField
        label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Dialog>
  );
}
